
import {defineComponent} from "vue";
import {CodeDescriptionRestDto, TitleMetaDataRestDto,} from "@/api/pharma-cms-content-block/models";
import {CbStructureMixin} from "@/components/layouts/content-pages/wysiwyg/structures/CbStructureMixin";
import {NormalContentBlockRestService} from "@/services/rest/cms-content-block/NormalContentBlockRestService";
import {FindNormalContentBlockPayload} from "@/models/payload/FindNormalContentBlockPayload";
import {CreateNormalContentBlockPayload} from "@/models/payload/CreateNormalContentBlockPayload";
import {SaveNormalContentBlockPayload} from "@/models/payload/SaveNormalContentBlockPayload";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import {ContentBlockTypesEnum} from "@/helpers/functions/enum";
import ProductFilterPicker from "@/components/UI/ProductFilterPicker.vue";
import {ProductFilterTypeEnum} from "@/models/enum/ProductFilterTypeEnum";
import {ProductFilterPickerPayload} from "@/models/payload/ProductFilterPickerPayload";
import {ProductFilterOverviewRestDto, ProductFilterRestDto} from "@/api/pharma-cpc-mgmt/models";
import ability from "@/config/ability";
import {generateUUIDv4} from "@/helpers/functions/string";
import {ProductFilterContentRestDtoModel} from "@/models/ProductFilterContentRestDtoModel";
import BaseSpinner from "@/components/UI/Bootstrap/BaseSpinner.vue";
import Subtitle from "@/components/UI/Subtitle.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import InputText from "@/components/UI/InputText.vue";
import InputTextEditor from "@/components/UI/InputTextEditor.vue";
import AdvancedPanelSwitcher from "@/components/UI/AdvancedPanelSwitcher.vue";
import TitleMetaInputComponent from "@/components/UI/TitleMetaInputComponent.vue";
import AdvancedPanel from "@/components/UI/AdvancedPanel.vue";
import {TitleMetaDataRestDtoModel} from "@/models/api/pharma-cms-content-block/TitleMetaDataRestDtoModel";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {ProductFilterRestDtoModel} from "@/models/api/pharma-cpc-mgmt/ProductFilterRestDtoModel";
import {ProductFiltersRestService} from "@/services/rest/product-filters/ProductFiltersRestService";
import {DpEvent} from "@/models/DpEvent";

export default defineComponent({
  name: "CbProductFilter",
  components: {
    AdvancedPanel,
    TitleMetaInputComponent, AdvancedPanelSwitcher, InputTextEditor, InputText, InputSelect, Subtitle, BaseSpinner, ProductFilterPicker},
  extends: CbStructureMixin,
  data: () => ({
    contentBlock: ProductFilterContentRestDtoModel.createWithDefaults(),
    currentProductFilterType: ProductFilterTypeEnum.CUSTOM as ProductFilterTypeEnum,
    productFilterPickerPayload: ProductFilterPickerPayload.createWithDefaults(),
    isAdvancedTitleMeta: false,
    enumTitleSizeCode: [] as CodeDescriptionRestDto[],
    productFilter: ProductFilterRestDtoModel.createWithDefaults() as ProductFilterRestDto,
    namedProductFilterOptions: [] as CodeDescriptionRestDto[]
  }),
  created() {
    this.initialize();
  },
  computed: {
    canUseNamedProductFilter(): boolean {
      return ability.can('use-feature', 'NAMED_PRODUCT_FILTER');
    },
    isPageTitle(): boolean {
      return this.cbType === ContentBlockTypesEnum.PAGE_TITLE;
    },
  },
  methods: {
    async find(payload: FindNormalContentBlockPayload): Promise<ProductFilterContentRestDtoModel> {
      return await NormalContentBlockRestService.getInstance().findProductFilter(payload.type, payload.id);
    },
    async create(payload: CreateNormalContentBlockPayload): Promise<string> {
      return await NormalContentBlockRestService.getInstance().createProductFilter(payload.cb as ProductFilterContentRestDtoModel, payload.type);
    },
    async save(payload: SaveNormalContentBlockPayload): Promise<void> {
      await NormalContentBlockRestService.getInstance().saveProductFilter(payload.cb as ProductFilterContentRestDtoModel, payload.type, payload.id);
    },
    async initialize(): Promise<void> {
      this.enumTitleSizeCode = await EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.TITLE_SIZE, DpModuleEnum.CMS);
    },
    getDefaultCbModel(): ProductFilterContentRestDtoModel {
      return ProductFilterContentRestDtoModel.createWithDefaults();
    },
    toggleAdvancedTitleMeta(): void {
      if (!this.isAdvancedTitleMeta && (this.contentBlock.title_meta === null || this.contentBlock.title_meta === undefined)) {
        this.contentBlock.title_meta = TitleMetaDataRestDtoModel.createWithDefaults();
      }
      this.isAdvancedTitleMeta = !this.isAdvancedTitleMeta;
    },
    onTitleMetaUpdate(payload: TitleMetaDataRestDto): void {
      this.contentBlock.title_meta = payload;
      this.onChange();
    },
    async beforeReloadContent(_event: DpEvent): Promise<void> {
      if (this.canUseNamedProductFilter) {
        const namedProductFilters: ProductFilterOverviewRestDto[] = await ProductFiltersRestService.getInstance().findProductFilters(true);
        this.namedProductFilterOptions = namedProductFilters
          .map((filter: ProductFilterOverviewRestDto) => {
            return {code: filter.code, description: filter.name};
          });
      }
    },
    async afterReloadContent() {
      if (this.contentBlock.title_meta) {
        this.isAdvancedTitleMeta = true;
      }
      this.productFilterPickerPayload = new ProductFilterPickerPayload(
        this.contentBlock.product_filter_code ?? null,
        this.namedProductFilterOptions
      );
      this.currentProductFilterType = this.productFilterPickerPayload.type;
      if (this.contentBlock.product_filter_code && this.currentProductFilterType === ProductFilterTypeEnum.CUSTOM) {
        this.productFilter = await ProductFiltersRestService.getInstance().findComplexProductFilter(this.contentBlock.product_filter_code);
      } else {
        this.productFilter = ProductFilterRestDtoModel.createWithDefaults();
      }
    },
    async beforeSubmitData() {
      if (this.currentProductFilterType === ProductFilterTypeEnum.CUSTOM) {
        if (!this.contentBlock.product_filter_code) {
          this.contentBlock.product_filter_code = generateUUIDv4();
        }
        await ProductFiltersRestService.getInstance().saveComplexProductFilter(this.productFilter, this.contentBlock.product_filter_code);
      }
    },
    onProductFilterPickerChanged(payload: ProductFilterPickerPayload): void {
      this.contentBlock.product_filter_code = payload.code;
      this.currentProductFilterType = payload.type;
      this.onChange();
    },
    onProductFilterChanged(filter: ProductFilterRestDto): void {
      this.productFilter = filter;
      this.onChange();
    },
    pasteCbMetadata(): void {
      this.contentBlock.cloneMetadataFrom(this.getClipboardContentBlock(this.cbType) as ProductFilterContentRestDtoModel);
    },
  }
});
